import { useState } from 'react';
import { OwnerLayout } from '../../components/layouts/OwnerLayout';
import { 
  BookOpen, Users, Calendar, FileText, Package, BarChart3, Settings, 
  ChevronDown, ChevronRight, PlayCircle, CheckCircle2, AlertCircle,
  UserPlus, ClipboardList, MapPin, Bell, CreditCard, HelpCircle,
  Smartphone, Monitor, ArrowRight, Lightbulb
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { cn } from '../../lib/utils';

const Guide = () => {
  const [openSections, setOpenSections] = useState({});

  const toggleSection = (id) => {
    setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const Section = ({ id, icon: Icon, title, children, badge }) => (
    <Collapsible open={openSections[id]} onOpenChange={() => toggleSection(id)}>
      <CollapsibleTrigger className="w-full">
        <div className="flex items-center justify-between p-4 bg-white rounded-lg border hover:border-sky-200 hover:bg-sky-50/50 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center">
              <Icon className="w-5 h-5 text-sky-600" />
            </div>
            <span className="font-medium text-slate-900">{title}</span>
            {badge && <Badge variant="secondary" className="ml-2">{badge}</Badge>}
          </div>
          {openSections[id] ? (
            <ChevronDown className="w-5 h-5 text-slate-400" />
          ) : (
            <ChevronRight className="w-5 h-5 text-slate-400" />
          )}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-2 p-4 bg-slate-50 rounded-lg border border-slate-100">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );

  const Step = ({ number, title, children }) => (
    <div className="flex gap-4 mb-4">
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-sky-600 text-white flex items-center justify-center font-bold text-sm">
        {number}
      </div>
      <div className="flex-1">
        <h4 className="font-medium text-slate-900 mb-1">{title}</h4>
        <div className="text-slate-600 text-sm">{children}</div>
      </div>
    </div>
  );

  const Tip = ({ children }) => (
    <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mt-3">
      <Lightbulb className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <p className="text-sm text-amber-800">{children}</p>
    </div>
  );

  return (
    <OwnerLayout>
      <div className="space-y-6" data-testid="guide-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-sky-500 to-cyan-600 flex items-center justify-center">
            <BookOpen className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Guía de Usuario</h1>
            <p className="text-slate-500">Aprende a usar todas las funciones de Aqua Nexia</p>
          </div>
        </div>

        {/* Quick Start */}
        <Card className="border-sky-200 bg-gradient-to-r from-sky-50 to-cyan-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sky-700">
              <PlayCircle className="w-5 h-5" />
              Inicio Rápido
            </CardTitle>
            <CardDescription>Los primeros pasos para comenzar a usar la aplicación</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-4 bg-white rounded-lg border">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-full bg-sky-600 text-white flex items-center justify-center text-xs font-bold">1</div>
                  <span className="font-medium">Configura tu empresa</span>
                </div>
                <p className="text-sm text-slate-600">Ve a Configuración para añadir el logo, datos fiscales y personalizar la aplicación.</p>
              </div>
              <div className="p-4 bg-white rounded-lg border">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-full bg-sky-600 text-white flex items-center justify-center text-xs font-bold">2</div>
                  <span className="font-medium">Añade tus clientes</span>
                </div>
                <p className="text-sm text-slate-600">Registra a tus clientes con sus datos de contacto y características de la piscina.</p>
              </div>
              <div className="p-4 bg-white rounded-lg border">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-full bg-sky-600 text-white flex items-center justify-center text-xs font-bold">3</div>
                  <span className="font-medium">Programa trabajos</span>
                </div>
                <p className="text-sm text-slate-600">Usa el calendario para programar mantenimientos y asignarlos a trabajadores.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Content - Tabs for Owner/Worker */}
        <Tabs defaultValue="owner" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="owner" className="flex items-center gap-2">
              <Monitor className="w-4 h-4" />
              Guía del Propietario
            </TabsTrigger>
            <TabsTrigger value="worker" className="flex items-center gap-2">
              <Smartphone className="w-4 h-4" />
              Guía del Trabajador
            </TabsTrigger>
          </TabsList>

          {/* Owner Guide */}
          <TabsContent value="owner" className="space-y-4">
            <Section id="clients" icon={Users} title="Gestión de Clientes" badge="Esencial">
              <Step number={1} title="Crear un nuevo cliente">
                Haz clic en <strong>"Clientes"</strong> en el menú lateral y luego en <strong>"Nuevo Cliente"</strong>.
                Completa los datos básicos: nombre (obligatorio), teléfono, email y dirección.
              </Step>
              <Step number={2} title="Información de la piscina">
                Añade el tipo de tratamiento (cloro, sal, bromo, etc.) y las dimensiones de la piscina.
                Esta información aparecerá en los partes de trabajo.
              </Step>
              <Step number={3} title="Envío automático de PDFs">
                Activa <strong>"Enviar informe por email"</strong> para que el cliente reciba 
                automáticamente el PDF del parte cada vez que se complete un trabajo.
              </Step>
              <Tip>
                Puedes usar el buscador de direcciones para autocompletar la ubicación del cliente.
                Esto facilita la optimización de rutas.
              </Tip>
            </Section>

            <Section id="workers" icon={UserPlus} title="Gestión de Trabajadores">
              <Step number={1} title="Invitar a un trabajador">
                Ve a <strong>"Trabajadores"</strong> y haz clic en <strong>"Invitar Trabajador"</strong>.
                Introduce su email y nombre. Recibirá un enlace para crear su cuenta.
              </Step>
              <Step number={2} title="Tipos de trabajador">
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li><strong>Operario:</strong> Solo ve sus propios trabajos asignados</li>
                  <li><strong>Administración:</strong> Acceso completo como el propietario (excepto facturación)</li>
                </ul>
              </Step>
              <Step number={3} title="Gestionar acceso">
                Puedes desactivar temporalmente o eliminar trabajadores desde la lista.
                Los trabajadores desactivados no pueden acceder a la aplicación.
              </Step>
              <Tip>
                El número de trabajadores está limitado según tu plan de suscripción.
                Consulta la página de Suscripción para ver tus límites.
              </Tip>
            </Section>

            <Section id="calendar" icon={Calendar} title="Calendario y Programación">
              <Step number={1} title="Crear un trabajo">
                Haz clic en cualquier día del calendario o usa el botón <strong>"Nuevo Trabajo"</strong>.
                Selecciona el cliente, tipo de trabajo, trabajador asignado y hora.
              </Step>
              <Step number={2} title="Trabajos recurrentes">
                Configura la repetición: semanal, quincenal, dos veces por semana o mensual.
                Se crearán automáticamente los próximos 12 trabajos.
              </Step>
              <Step number={3} title="Asignar plantilla">
                Selecciona una <strong>"Plantilla para el parte"</strong> al crear el trabajo.
                El trabajador verá automáticamente el checklist y parámetros de esa plantilla.
              </Step>
              <Step number={4} title="Trabajos de urgencia">
                Marca <strong>"Es urgencia"</strong> para que el trabajador reciba una notificación
                prioritaria. Estos trabajos aparecen destacados en rojo.
              </Step>
              <Tip>
                Como propietario, también puedes asignarte trabajos a ti mismo 
                y completarlos desde "Mis Trabajos" en el Dashboard.
              </Tip>
            </Section>

            <Section id="templates" icon={ClipboardList} title="Plantillas de Trabajo">
              <Step number={1} title="Crear una plantilla">
                Ve a <strong>"Plantillas"</strong> y crea plantillas personalizadas para diferentes 
                tipos de mantenimiento. Incluye checklist, parámetros a medir y colores.
              </Step>
              <Step number={2} title="Checklist personalizado">
                Añade las tareas que el trabajador debe completar: limpieza de skimmers,
                cepillado, aspirado, revisión del filtro, etc.
              </Step>
              <Step number={3} title="Parámetros del agua">
                Define los parámetros a medir (pH, cloro, temperatura) con sus rangos
                mínimos y máximos. Se mostrarán avisos si los valores están fuera de rango.
              </Step>
              <Tip>
                Al registrarte se crean 4 plantillas predeterminadas que puedes usar como base.
                La plantilla "Mantenimiento Completo" sigue el RD 742/2013.
              </Tip>
            </Section>

            <Section id="materials" icon={Package} title="Inventario y Materiales">
              <Step number={1} title="Añadir materiales">
                Ve a <strong>"Materiales"</strong> y registra tus productos: cloro, alguicida,
                regulador de pH, etc. Indica la unidad (kg, L, unidades).
              </Step>
              <Step number={2} title="Control de stock">
                Cuando un trabajador completa un parte y registra materiales usados,
                el stock se descuenta automáticamente.
              </Step>
              <Step number={3} title="Alertas de stock bajo">
                Configura un <strong>"Stock mínimo"</strong> para cada material.
                Recibirás un email cuando el stock baje de ese nivel.
              </Step>
              <Tip>
                Añade el coste unitario de cada material para ver análisis de rentabilidad
                en la sección de Analytics.
              </Tip>
            </Section>

            <Section id="reports" icon={FileText} title="Partes de Trabajo">
              <Step number={1} title="Ver partes completados">
                En <strong>"Reportes"</strong> puedes ver todos los partes por mes o por cliente.
                Filtra por trabajador para analizar el rendimiento individual.
              </Step>
              <Step number={2} title="Editar un parte">
                Haz clic en <strong>"Editar"</strong> para modificar cualquier dato del parte:
                tiempos, checklist, parámetros, materiales u observaciones.
                Los cambios quedan registrados en el historial.
              </Step>
              <Step number={3} title="Descargar PDF">
                Cada parte genera un PDF profesional con el logo de tu empresa,
                todos los datos del trabajo, fotos y firma del técnico.
              </Step>
              <Step number={4} title="Eliminar partes">
                Solo el propietario puede eliminar partes. El trabajo asociado
                volverá al estado "Programado".
              </Step>
              <Tip>
                Puedes ver el historial de modificaciones de cada parte haciendo clic
                en el icono de historial si ha sido editado.
              </Tip>
            </Section>

            <Section id="analytics" icon={BarChart3} title="Analytics y Estadísticas">
              <Step number={1} title="Panel de rentabilidad">
                La sección <strong>"Analytics"</strong> muestra ingresos estimados, costes de materiales
                y rentabilidad por cliente o período.
              </Step>
              <Step number={2} title="Historial de parámetros">
                Visualiza la evolución del pH, cloro y otros parámetros de cada piscina
                a lo largo del tiempo en gráficos interactivos.
              </Step>
              <Step number={3} title="Resumen mensual">
                En "Reportes" accede al resumen mensual: total de partes, trabajadores activos
                y materiales utilizados.
              </Step>
            </Section>

            <Section id="routes" icon={MapPin} title="Optimización de Rutas">
              <Step number={1} title="Planificar ruta">
                Ve a <strong>"Rutas"</strong>, selecciona una fecha y los trabajos del día
                aparecerán en el mapa.
              </Step>
              <Step number={2} title="Optimizar recorrido">
                Haz clic en <strong>"Optimizar Ruta"</strong> para calcular el orden más
                eficiente de visitas, minimizando tiempo y distancia.
              </Step>
              <Step number={3} title="Ver direcciones">
                El sistema te muestra el recorrido completo con las instrucciones
                de navegación paso a paso.
              </Step>
              <Tip>
                Asegúrate de que tus clientes tengan la dirección correctamente introducida
                para que la optimización funcione bien.
              </Tip>
            </Section>

            <Section id="subscription" icon={CreditCard} title="Suscripción y Facturación">
              <Step number={1} title="Planes disponibles">
                <ul className="list-disc list-inside space-y-1 mt-2">
                  <li><strong>Autónomo (29€/mes):</strong> 1 usuario, hasta 50 clientes</li>
                  <li><strong>PYME (39€/mes):</strong> 4 usuarios, hasta 150 clientes</li>
                  <li><strong>Empresa (49€/mes):</strong> Usuarios y clientes ilimitados</li>
                </ul>
              </Step>
              <Step number={2} title="Período de prueba">
                Al registrarte tienes 14 días de prueba con todas las funciones del plan
                que elijas. No se cobra hasta que termina la prueba.
              </Step>
              <Step number={3} title="Gestionar suscripción">
                Desde la página de <strong>"Suscripción"</strong> puedes cambiar de plan,
                actualizar tu método de pago o cancelar.
              </Step>
            </Section>

            <Section id="settings" icon={Settings} title="Configuración">
              <Step number={1} title="Datos de empresa">
                Sube tu logo, configura el nombre, dirección, teléfono, email y CIF
                de tu empresa. Estos datos aparecen en los PDFs.
              </Step>
              <Step number={2} title="Personalizar PDFs">
                Añade texto personalizado en la cabecera y pie de página de los informes PDF.
              </Step>
              <Step number={3} title="Notificaciones">
                Configura si quieres recibir notificaciones por email cuando haya
                alertas de stock u otros eventos.
              </Step>
            </Section>
          </TabsContent>

          {/* Worker Guide */}
          <TabsContent value="worker" className="space-y-4">
            <Card className="border-green-200 bg-gradient-to-r from-green-50 to-emerald-50 mb-6">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <Smartphone className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-medium text-green-800">Interfaz optimizada para móvil</h3>
                    <p className="text-sm text-green-700 mt-1">
                      La aplicación está diseñada para usarse cómodamente desde el teléfono
                      mientras estás en el trabajo de campo.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Section id="worker-start" icon={PlayCircle} title="Primeros Pasos">
              <Step number={1} title="Aceptar la invitación">
                Recibirás un email con un enlace para unirte al equipo.
                Haz clic y crea tu contraseña para activar tu cuenta.
              </Step>
              <Step number={2} title="Iniciar sesión">
                Accede con tu email y contraseña. Verás tu panel con los trabajos asignados.
              </Step>
              <Step number={3} title="Navegar por la app">
                Usa el menú inferior para acceder a: Trabajos, Calendario, Nuevo Parte e Historial.
              </Step>
            </Section>

            <Section id="worker-jobs" icon={ClipboardList} title="Ver y Gestionar Trabajos">
              <Step number={1} title="Trabajos del día">
                En la pantalla principal verás los trabajos programados para hoy,
                ordenados por hora.
              </Step>
              <Step number={2} title="Detalles del trabajo">
                Toca un trabajo para ver la información completa: cliente, dirección,
                tipo de trabajo y plantilla asignada.
              </Step>
              <Step number={3} title="Iniciar trabajo">
                Pulsa <strong>"Iniciar"</strong> cuando llegues al cliente.
                Esto registra la hora de inicio.
              </Step>
              <Step number={4} title="Ver ubicación">
                Usa el botón de mapa para abrir la navegación GPS hasta la dirección del cliente.
              </Step>
            </Section>

            <Section id="worker-report" icon={FileText} title="Completar un Parte">
              <Step number={1} title="Checklist">
                Marca las tareas completadas de la lista. Puedes añadir notas
                a cada tarea si es necesario.
              </Step>
              <Step number={2} title="Parámetros del agua">
                Introduce las mediciones de pH, cloro, temperatura y otros parámetros.
                Los valores fuera de rango se mostrarán en rojo.
              </Step>
              <Step number={3} title="Materiales utilizados">
                Selecciona los productos usados y la cantidad. Esto actualiza
                automáticamente el inventario.
              </Step>
              <Step number={4} title="Fotos">
                Toma fotos del trabajo realizado. Puedes añadir hasta 5 fotos
                que aparecerán en el informe PDF.
              </Step>
              <Step number={5} title="Observaciones">
                Añade cualquier comentario relevante: problemas detectados,
                recomendaciones para el cliente, etc.
              </Step>
              <Step number={6} title="Finalizar">
                Pulsa <strong>"Enviar Parte"</strong> para completar el trabajo.
                Si el cliente tiene activo el envío por email, recibirá el PDF automáticamente.
              </Step>
              <Tip>
                Si necesitas indicar una duración diferente a la calculada automáticamente,
                usa el campo "Duración manual" antes de enviar.
              </Tip>
            </Section>

            <Section id="worker-adhoc" icon={AlertCircle} title="Partes Ad-hoc (Sin cita)">
              <Step number={1} title="Crear parte rápido">
                Desde el menú, ve a <strong>"Nuevo Parte"</strong> para crear un parte
                sin trabajo previo programado.
              </Step>
              <Step number={2} title="Seleccionar cliente">
                Elige el cliente de la lista o crea uno nuevo si no existe.
              </Step>
              <Step number={3} title="Completar el parte">
                El proceso es igual que un parte normal: checklist, parámetros,
                materiales, fotos y observaciones.
              </Step>
              <Tip>
                Los partes ad-hoc son útiles para trabajos de emergencia o visitas
                no programadas.
              </Tip>
            </Section>

            <Section id="worker-calendar" icon={Calendar} title="Calendario del Trabajador">
              <Step number={1} title="Vista mensual">
                El calendario muestra los días con trabajos asignados marcados.
                Los puntos rojos indican urgencias.
              </Step>
              <Step number={2} title="Ver trabajos del día">
                Toca un día para ver todos los trabajos programados.
              </Step>
            </Section>

            <Section id="worker-history" icon={FileText} title="Historial de Partes">
              <Step number={1} title="Ver partes anteriores">
                En <strong>"Historial"</strong> puedes ver todos los partes que has completado.
              </Step>
              <Step number={2} title="Filtrar por fecha">
                Usa los filtros para buscar partes de fechas o clientes específicos.
              </Step>
              <Step number={3} title="Descargar PDF">
                Puedes descargar el PDF de cualquier parte anterior.
              </Step>
            </Section>

            <Section id="worker-chat" icon={HelpCircle} title="Asistente IA">
              <Step number={1} title="Hacer consultas">
                El asistente puede responder preguntas sobre mantenimiento de piscinas,
                dosificación de productos, solución de problemas, etc.
              </Step>
              <Step number={2} title="Adjuntar fotos">
                Puedes enviar fotos al asistente para que te ayude a diagnosticar
                problemas del agua o equipos.
              </Step>
            </Section>
          </TabsContent>
        </Tabs>

        {/* FAQ Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-sky-600" />
              Preguntas Frecuentes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium mb-2">¿Puedo usar la app sin conexión a internet?</h4>
              <p className="text-sm text-slate-600">
                Actualmente la aplicación requiere conexión a internet. Estamos trabajando
                en una versión móvil con modo offline para el futuro.
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium mb-2">¿Cómo cambio mi contraseña?</h4>
              <p className="text-sm text-slate-600">
                Ve a Configuración → Mi Perfil y encontrarás la opción para cambiar tu contraseña.
                También puedes usar "¿Olvidaste tu contraseña?" en la pantalla de login.
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium mb-2">¿Puedo modificar un parte ya completado?</h4>
              <p className="text-sm text-slate-600">
                Sí, todos los usuarios pueden editar partes completados. Los cambios quedan
                registrados en el historial. Solo el propietario puede eliminar partes.
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium mb-2">¿Cómo cancelo mi suscripción?</h4>
              <p className="text-sm text-slate-600">
                Ve a Suscripción → Gestionar Suscripción. Desde el portal de pagos puedes
                cancelar en cualquier momento. Mantendrás el acceso hasta el fin del período facturado.
              </p>
            </div>
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium mb-2">¿Los datos de mis clientes están seguros?</h4>
              <p className="text-sm text-slate-600">
                Sí, todos los datos se almacenan de forma segura con encriptación.
                Solo tú y los trabajadores de tu empresa pueden acceder a la información.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Contact Support */}
        <Card className="border-sky-200">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left">
              <div className="w-16 h-16 rounded-full bg-sky-100 flex items-center justify-center">
                <HelpCircle className="w-8 h-8 text-sky-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg">¿Necesitas más ayuda?</h3>
                <p className="text-slate-600">
                  Si tienes alguna duda o problema que no encuentras en esta guía,
                  no dudes en contactarnos.
                </p>
              </div>
              <a 
                href="mailto:soporte@aquanexia.com"
                className="px-6 py-3 bg-sky-600 text-white rounded-lg font-medium hover:bg-sky-700 transition-colors"
              >
                Contactar Soporte
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </OwnerLayout>
  );
};

export default Guide;
